import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useProperty } from "@/context/PropertyContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Download, Trash2, RotateCcw, AlertTriangle, Building2, FolderOpen, Clock, Play, UploadCloud } from "lucide-react";
import { toast } from "sonner";

export default function Backups() {
  const { user } = useAuth();
  const { properties, reload: reloadProperties } = useProperty();
  const isAdmin = user?.role === "admin";
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(null);
  const [fullBackupLoading, setFullBackupLoading] = useState(false);
  const importRef = useRef(null);

  const [selBackup, setSelBackup] = useState(null);
  const [selProps, setSelProps] = useState([]);
  const [previewProps, setPreviewProps] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [exportOpen, setExportOpen] = useState(false);
  const [exportSelProps, setExportSelProps] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);

  const [schedule, setSchedule] = useState(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/backup/list")
      .then((r) => setBackups(r.data))
      .catch(() => toast.error("Error al cargar backups"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/backup/schedule").then((r) => setSchedule(r.data));
  }, [isAdmin]);

  const saveSchedule = async () => {
    if (!schedule) return;
    setScheduleLoading(true);
    try {
      const { data } = await api.put("/backup/schedule", schedule);
      setSchedule(data);
      toast.success("Programación guardada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setScheduleLoading(false);
    }
  };

  const runBackupNow = async () => {
    setScheduleLoading(true);
    try {
      const { data } = await api.post("/backup/run");
      toast.success(`Backup creado: ${data.filename}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al generar backup");
    } finally {
      setScheduleLoading(false);
    }
  };

  const openExport = () => {
    setExportSelProps([]);
    setExportOpen(true);
  };

  const toggleExportProp = (id) =>
    setExportSelProps((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const toggleExportAll = () =>
    setExportSelProps((prev) => (prev.length === properties.length ? [] : properties.map((p) => p.id)));

  const execExport = async (format) => {
    if (exportSelProps.length === 0) return toast.error("Selecciona al menos un piso");
    setExportLoading(true);
    try {
      // Si un admin marca literalmente todos los pisos, se exporta sin
      // filtro (incluye también los ajustes globales, que no son de ningún
      // piso concreto). Para un no-admin da igual: el backend siempre acota
      // a sus propios pisos.
      const allSelected = isAdmin && exportSelProps.length === properties.length;
      const idsParam = allSelected ? "" : `&property_ids=${exportSelProps.join(",")}`;
      const { data, headers } = await api.get(`/backup/export?format=${format}${idsParam}`, { responseType: "blob" });
      const disposition = headers?.["content-disposition"] || "";
      const nameMatch = disposition.match(/filename=([^;]+)/i);
      const filename = nameMatch?.[1]?.replaceAll('"', "") || `rentapiso_backup.${format}`;
      const url = window.URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Backup descargado");
      setExportOpen(false);
    } catch {
      toast.error("Error al exportar el backup");
    } finally {
      setExportLoading(false);
    }
  };

  const importFullBackup = async (file) => {
    if (!file) return;
    if (!/\.(json|zip)$/i.test(file.name)) return toast.error("Selecciona un archivo .json o .zip");
    if (!window.confirm("Esto reemplazará pisos, inquilinos, contratos, pagos, gastos e históricos. ¿Continuar?")) return;
    setFullBackupLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/backup/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const total = Object.values(data.imported || {}).reduce((a, b) => a + Number(b || 0), 0);
      toast.success(`Backup importado (${total} registros)`);
      load();
      await reloadProperties();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al importar el backup");
    } finally {
      setFullBackupLoading(false);
      if (importRef.current) importRef.current.value = "";
    }
  };

  const openSelective = async (filename) => {
    setSelBackup(filename);
    setSelProps([]);
    if (!isAdmin) {
      // No hace falta leer el backup: un no-admin solo puede elegir entre
      // sus propios pisos, que ya conocemos por contexto.
      setPreviewProps(properties.map((p) => ({ id: p.id, nombre: p.nombre })));
      return;
    }
    setPreviewLoading(true);
    try {
      const { data } = await api.get(`/backup/preview/${encodeURIComponent(filename)}`);
      setPreviewProps(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al leer el backup");
      setSelBackup(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const toggleSelProp = (id) =>
    setSelProps((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const toggleSelAll = () =>
    setSelProps((prev) => (prev.length === previewProps.length ? [] : previewProps.map((p) => p.id)));

  const execSelectiveRestore = async () => {
    if (selProps.length === 0) return toast.error("Selecciona al menos un piso");
    if (!window.confirm(
      `Se reemplazarán los datos de ${selProps.length} piso(s) y todo su contenido.\n\n` +
      "Los datos actuales de esos pisos se perderán.\n¿Continuar?"
    )) return;
    setRestoring(selBackup);
    try {
      const { data } = await api.post(`/backup/restore-user?filename=${encodeURIComponent(selBackup)}`, { property_ids: selProps });
      const total = Object.values(data.restored || {}).reduce((a, b) => a + Number(b || 0), 0);
      toast.success(`Restaurados ${selProps.length} piso(s) (${total} registros)`);
      setSelBackup(null);
      setSelProps([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al restaurar");
    } finally {
      setRestoring(null);
    }
  };

  const downloadFile = (filename) => {
    const a = document.createElement("a");
    a.href = `${api.defaults.baseURL}/backup/download/${encodeURIComponent(filename)}`;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const deleteFile = async (filename) => {
    if (!window.confirm(`Eliminar ${filename}?`)) return;
    try {
      await api.delete(`/backup/${encodeURIComponent(filename)}`);
      toast.success("Backup eliminado");
      load();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  const fmtSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const fmtDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div data-testid="backups-page">
      <PageHeader title="Copias de seguridad" subtitle="Backups automáticos programados por el administrador">
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RotateCcw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Recargar
        </Button>
      </PageHeader>

      {!isAdmin && (
        <div className="flex items-start gap-3 p-4 mb-6 rounded-md border border-warning/40 bg-warning/5 text-sm">
          <AlertTriangle className="h-5 w-5 text-warning shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-foreground">Restaurar solo tus datos</p>
            <p className="text-muted-foreground mt-1">
              Puedes restaurar, piso a piso, la información de los pisos que administras.
              Si alguno está compartido con otro usuario, no podrás restaurarlo tú solo — pide al administrador que lo haga.
            </p>
          </div>
        </div>
      )}

      <div className="data-card p-5 mb-5">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h3 className="font-display font-medium text-lg">Backup de datos</h3>
            <p className="text-sm text-muted-foreground mt-1">
              {isAdmin
                ? "Exporta (todo o pisos concretos) o importa un backup completo — inquilinos, contratos, pagos, gastos, firma e históricos de contratos."
                : "Exporta los pisos que administras — inquilinos, contratos, pagos, gastos e históricos de contratos."}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={openExport} data-testid="backup-export-button">
              <Download className="h-4 w-4 mr-1.5" /> Exportar
            </Button>
            {isAdmin && (
              <>
                <input ref={importRef} type="file" accept=".json,.zip" className="hidden" onChange={(e) => importFullBackup(e.target.files?.[0])} />
                <Button onClick={() => importRef.current?.click()} disabled={fullBackupLoading} data-testid="backup-import-button">
                  <UploadCloud className="h-4 w-4 mr-1.5" /> Importar
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {isAdmin && (
        <div className="data-card p-5 mb-5">
          <div className="flex flex-col gap-2 mb-5">
            <h3 className="font-display font-medium text-lg flex items-center gap-2">
              <Clock className="h-5 w-5 text-muted-foreground" /> Programar backups automáticos
            </h3>
            <p className="text-sm text-muted-foreground">Los backups se guardan en el servidor y son visibles para todos los usuarios.</p>
          </div>

          {schedule ? (
            <div className="space-y-4 max-w-xl">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-border accent-primary"
                  checked={schedule.enabled}
                  onChange={(e) => setSchedule((s) => ({ ...s, enabled: e.target.checked }))}
                />
                <span className="text-sm font-medium">Backups automáticos activados</span>
              </label>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <p className="overline mb-1.5">Frecuencia</p>
                  <select
                    className="w-full h-10 rounded-md border border-border bg-card px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    value={schedule.frequency}
                    onChange={(e) => setSchedule((s) => ({ ...s, frequency: e.target.value }))}
                  >
                    <option value="daily">Diaria</option>
                    <option value="weekly">Semanal</option>
                    <option value="monthly">Mensual</option>
                  </select>
                </div>
                <div>
                  <p className="overline mb-1.5">Hora</p>
                  <input
                    type="time"
                    className="w-full h-10 rounded-md border border-border bg-card px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    value={schedule.time}
                    onChange={(e) => setSchedule((s) => ({ ...s, time: e.target.value }))}
                  />
                </div>
                <div>
                  <p className="overline mb-1.5">Retener</p>
                  <select
                    className="w-full h-10 rounded-md border border-border bg-card px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    value={schedule.retention}
                    onChange={(e) => setSchedule((s) => ({ ...s, retention: Number(e.target.value) }))}
                  >
                    {[5, 10, 20, 30, 50].map((n) => <option key={n} value={n}>{n} backups</option>)}
                  </select>
                </div>
              </div>

              {schedule.frequency === "weekly" && (
                <div>
                  <p className="overline mb-1.5">Día de la semana</p>
                  <select
                    className="w-full h-10 rounded-md border border-border bg-card px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    value={schedule.day_of_week}
                    onChange={(e) => setSchedule((s) => ({ ...s, day_of_week: e.target.value }))}
                  >
                    <option value="mon">Lunes</option>
                    <option value="tue">Martes</option>
                    <option value="wed">Miércoles</option>
                    <option value="thu">Jueves</option>
                    <option value="fri">Viernes</option>
                    <option value="sat">Sábado</option>
                    <option value="sun">Domingo</option>
                  </select>
                </div>
              )}

              {schedule.frequency === "monthly" && (
                <div>
                  <p className="overline mb-1.5">Día del mes</p>
                  <select
                    className="w-full h-10 rounded-md border border-border bg-card px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    value={schedule.day_of_month}
                    onChange={(e) => setSchedule((s) => ({ ...s, day_of_month: Number(e.target.value) }))}
                  >
                    {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <Button onClick={saveSchedule} disabled={scheduleLoading}>
                  {scheduleLoading ? "Guardando…" : "Guardar programación"}
                </Button>
                <Button variant="outline" onClick={runBackupNow} disabled={scheduleLoading}>
                  <Play className="h-4 w-4 mr-1.5" /> Ejecutar backup ahora
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Cargando configuración…</p>
          )}
        </div>
      )}

      <div className="data-card overflow-hidden">
        <Table data-testid="backups-table">
          <TableHeader><TableRow>
            <TableHead>Archivo</TableHead>
            <TableHead>Fecha</TableHead>
            <TableHead className="text-right">Tamaño</TableHead>
            <TableHead className="w-64 text-right">Acciones</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {backups.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                  {loading ? "Cargando…" : isAdmin ? "No hay copias de seguridad. Prográmalas arriba o pulsa \"Ejecutar backup ahora\"." : "No hay copias de seguridad. El administrador puede programarlas."}
                </TableCell>
              </TableRow>
            ) : (
              backups.map((b) => (
                <TableRow key={b.filename}>
                  <TableCell className="font-mono text-sm">{b.filename}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{fmtDate(b.modified)}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{fmtSize(b.size)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-1 justify-end">
                      {isAdmin && (
                        <Button variant="ghost" size="sm" onClick={() => downloadFile(b.filename)} title="Descargar">
                          <Download className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="outline" size="sm"
                        onClick={() => openSelective(b.filename)}
                        title="Restauración selectiva de pisos"
                        className="text-primary border-primary/40 hover:bg-primary/10"
                      >
                        <FolderOpen className="h-4 w-4 mr-1" /> Restaurar
                      </Button>
                      {isAdmin && (
                        <Button variant="ghost" size="sm" onClick={() => deleteFile(b.filename)} title="Eliminar" className="text-destructive hover:text-destructive">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!selBackup} onOpenChange={(o) => !o && setSelBackup(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Restauración selectiva</DialogTitle>
            <DialogDescription>
              Selecciona los pisos que quieres restaurar desde <span className="font-mono text-xs">{selBackup}</span>.
              Los datos actuales de esos pisos se reemplazarán.
            </DialogDescription>
          </DialogHeader>
          {previewLoading ? (
            <p className="text-sm text-muted-foreground py-4">Leyendo backup…</p>
          ) : previewProps.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">No se encontraron pisos en este backup.</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto py-2">
              <button
                type="button"
                onClick={toggleSelAll}
                className="text-xs text-primary hover:underline"
              >
                {selProps.length === previewProps.length ? "Deseleccionar todos" : "Seleccionar todos"}
              </button>
              {previewProps.map((p) => (
                <label key={p.id} className="flex items-center gap-3 rounded-md border border-border p-2.5 cursor-pointer hover:border-primary transition-colors">
                  <Checkbox checked={selProps.includes(p.id)} onCheckedChange={() => toggleSelProp(p.id)} />
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">{p.nombre}</span>
                </label>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelBackup(null)}>Cancelar</Button>
            <Button onClick={execSelectiveRestore} disabled={selProps.length === 0 || !!restoring}>
              {restoring ? "Restaurando…" : `Restaurar (${selProps.length})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={exportOpen} onOpenChange={(o) => !o && setExportOpen(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Exportar backup</DialogTitle>
            <DialogDescription>
              Selecciona los pisos que quieres incluir en el fichero exportado.
            </DialogDescription>
          </DialogHeader>
          {properties.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">No tienes pisos disponibles para exportar.</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto py-2">
              <button
                type="button"
                onClick={toggleExportAll}
                className="text-xs text-primary hover:underline"
              >
                {exportSelProps.length === properties.length ? "Deseleccionar todos" : "Seleccionar todos"}
              </button>
              {properties.map((p) => (
                <label key={p.id} className="flex items-center gap-3 rounded-md border border-border p-2.5 cursor-pointer hover:border-primary transition-colors">
                  <Checkbox checked={exportSelProps.includes(p.id)} onCheckedChange={() => toggleExportProp(p.id)} />
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">{p.nombre}</span>
                </label>
              ))}
            </div>
          )}
          <DialogFooter className="sm:justify-between">
            <Button variant="outline" onClick={() => setExportOpen(false)}>Cancelar</Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => execExport("json")} disabled={exportSelProps.length === 0 || exportLoading} data-testid="backup-export-json-button">
                {exportLoading ? "Exportando…" : "JSON"}
              </Button>
              <Button onClick={() => execExport("zip")} disabled={exportSelProps.length === 0 || exportLoading} data-testid="backup-export-zip-button">
                {exportLoading ? "Exportando…" : "ZIP"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
